/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import React, { useEffect, useMemo, useState } from "react";
import ReactApexChart from "react-apexcharts";
import { useNavigate } from "react-router-dom";

interface DonutChartProps {
  data: Record<string, string | number>[];
  label: string;
  series: string;
  dataKey: string;
  queryString?: string;
  disabledNavigation?: boolean;
}

export default function DonutChart({
  data,
  label,
  series,
  dataKey,
  queryString,
  disabledNavigation,
}: Readonly<DonutChartProps>) {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);

  // Memoize chart series based on data
  const chartSeries = useMemo(() => {
    return data.length ? data.map((item) => Number(item[series])) : [0];
  }, [data, series]);

  // Memoize chart labels and ensure they are strings
  const chartLabels = useMemo(() => {
    return data.length ? data.map((item) => String(item[label])) : [""];
  }, [data, label]);

  // Memoize chart options to avoid recreating them on every render
  const chartOptions = useMemo<ApexCharts.ApexOptions>(
    () => ({
      colors: [
        "#01372B",
        "#014C3C",
        "#016D56",
        "#4D9989",
        "#99C5BB",
        "#E6F0EE",
      ],
      labels: chartLabels, // Use memoized labels
      chart: {
        width: 320,
        type: "donut",
        events: {
          dataPointSelection: (event, chartContext, options) => {
            const clickedPoint = options?.dataPointIndex;

            // Validate clickedPoint and data
            if (
              clickedPoint === undefined ||
              !data.length ||
              !chartLabels[clickedPoint]
            ) {
              // eslint-disable-next-line no-console
              console.warn(
                "Clicked data is out of bounds or undefined",
                clickedPoint
              );
              return;
            }

            const customerId = data[clickedPoint]?.[dataKey];
            if (!disabledNavigation) {
              if (customerId) {
                setTimeout(() => {
                  // eslint-disable-next-line no-console
                  console.log("Clicked Value:", dataKey, customerId);
                  navigate(
                    `/dashboard-detail?${dataKey}=${customerId}${queryString}`
                  );
                }, 100);
              }
            }
          },
        },
      },
      dataLabels: {
        enabled: false,
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            chart: {
              width: 200,
            },
          },
        },
      ],
      legend: {
        show: false,
      },
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data, dataKey, chartLabels, queryString]
  ); // Include chartLabels in dependencies

  // Update state only when necessary
  const [state, setState] = useState({
    options: chartOptions,
    series: chartSeries,
  });

  useEffect(() => {
    if (data.length) {
      setIsLoading(false);
      setState({
        options: { ...chartOptions, labels: chartLabels },
        series: chartSeries,
      });
    }
  }, [data, chartLabels, chartSeries, chartOptions]);

  if (isLoading) {
    return <div />;
  }

  return (
    <ReactApexChart
      options={state.options}
      series={state.series}
      type="donut"
      width="100%"
    />
  );
}
